import api from './api';
import { endpoints } from './endpoints';
import { sanitizeAppPreferences } from '../utils/appPreferences';

function safeNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function safeStr(v, fallback = '') {
  if (v == null) return fallback;
  return String(v);
}

function asArray(v) {
  return Array.isArray(v) ? v : [];
}

function unwrapData(res) {
  if (res && typeof res === 'object' && res.data != null) return res.data;
  return res;
}

/**
 * Laravel ba'zan `{ data: { data: {...} } }` yoki boshqa o‘ramda qaytaradi.
 * Operatsion obyekt (pricingMode, branchPaymentMethods, …) topilguncha `data` bo‘yicha tushamiz.
 */
function unwrapOperationalPayload(res) {
  let cur = res;
  for (let depth = 0; depth < 10; depth += 1) {
    if (cur == null || typeof cur !== 'object' || Array.isArray(cur)) {
      return {};
    }
    const hasField =
      cur.branchPaymentMethods != null ||
      cur.branch_payment_methods != null ||
      cur.quickDiscounts != null ||
      cur.quick_discounts != null ||
      cur.pricingMode != null ||
      cur.pricing_mode != null ||
      cur.enabledSaleUnits != null ||
      cur.enabled_sale_units != null;
    if (hasField) {
      return cur;
    }
    if (cur.data != null && typeof cur.data === 'object' && !Array.isArray(cur.data)) {
      cur = cur.data;
      continue;
    }
    return cur;
  }
  return {};
}

function pick(raw, camelKey, snakeKey, fallback = undefined) {
  if (!raw || typeof raw !== 'object') return fallback;
  if (raw[camelKey] !== undefined) return raw[camelKey];
  if (raw[snakeKey] !== undefined) return raw[snakeKey];
  return fallback;
}

export async function getSystemDashboard(params = {}) {
  const res = await api.get(endpoints.systemDashboard, { params });
  const raw = unwrapData(res) || {};
  return {
    version: safeStr(raw.version, ''),
    uptime: safeStr(raw.uptime, ''),
    lastBackup: safeStr(raw.last_backup ?? raw.lastBackup, ''),
    dbStatus: safeStr(raw.db_status ?? raw.dbStatus, ''),
    apiRequests: safeNum(raw.api_requests ?? raw.apiRequests, 0),
    activeSessions: safeNum(raw.active_sessions ?? raw.activeSessions, 0),
    storageGb: safeNum(raw.storage_gb ?? raw.storageGb, 0),
    lastLogin: safeStr(raw.last_login ?? raw.lastLogin, ''),
    failedAttempts: safeNum(raw.failed_attempts ?? raw.failedAttempts, 0),
    activityKeys: asArray(raw.activity_keys ?? raw.activityKeys),
  };
}

export async function getSystemHealth(params = {}) {
  const res = await api.get(endpoints.systemHealth, { params });
  const raw = unwrapData(res) || {};
  return {
    overallStatus: safeStr(raw.overall_status ?? raw.overallStatus, 'operational'),
    version: safeStr(raw.version, ''),
    environment: safeStr(raw.environment, ''),
    uptimePercent: safeNum(raw.uptime_percent ?? raw.uptimePercent, 0),
    uptimeDays: safeNum(raw.uptime_days ?? raw.uptimeDays, 0),
    lastBackup: safeStr(raw.last_backup ?? raw.lastBackup, ''),
    lastDeploy: safeStr(raw.last_deploy ?? raw.lastDeploy, ''),
    services: asArray(raw.services),
    resources: raw.resources && typeof raw.resources === 'object' ? raw.resources : { cpu: 0, memory: 0, disk: 0 },
    incidents: asArray(raw.incidents),
  };
}

export async function getUsageMetrics(params = {}) {
  const res = await api.get(endpoints.usageMetrics, { params });
  const raw = unwrapData(res) || {};
  return {
    period: safeStr(raw.period, ''),
    apiRequestsTotal: safeNum(raw.api_requests_total ?? raw.apiRequestsTotal, 0),
    apiRequestsToday: safeNum(raw.api_requests_today ?? raw.apiRequestsToday, 0),
    activeUsers: safeNum(raw.active_users ?? raw.activeUsers, 0),
    storageUsedGb: safeNum(raw.storage_used_gb ?? raw.storageUsedGb, 0),
    storageLimitGb: safeNum(raw.storage_limit_gb ?? raw.storageLimitGb, 0),
    tenantsCount: safeNum(raw.tenants_count ?? raw.tenantsCount, 0),
    avgResponseMs: safeNum(raw.avg_response_ms ?? raw.avgResponseMs, 0),
    limits: raw.limits && typeof raw.limits === 'object' ? raw.limits : {},
    topEndpoints: asArray(raw.top_endpoints ?? raw.topEndpoints),
    dailyTrend: asArray(raw.daily_trend ?? raw.dailyTrend),
  };
}

export async function getSystemSettings(params = {}) {
  const res = await api.get(endpoints.settings, { params });
  const raw = unwrapData(res) || {};
  return {
    meta: raw.meta && typeof raw.meta === 'object' ? raw.meta : {},
    values: raw.values && typeof raw.values === 'object' ? raw.values : raw,
  };
}

export async function getOperationalSettings() {
  const res = await api.get(endpoints.settingsOperational);
  const raw = unwrapOperationalPayload(res) || {};
  const normalized = {
    pricingMode: pick(raw, 'pricingMode', 'pricing_mode'),
    defaultMarkupPercent: pick(raw, 'defaultMarkupPercent', 'default_markup_percent'),
    dailySummaryTime: pick(raw, 'dailySummaryTime', 'daily_summary_time'),
    enabledSaleUnits: pick(raw, 'enabledSaleUnits', 'enabled_sale_units'),
    purchaseCurrency: pick(raw, 'purchaseCurrency', 'purchase_currency'),
    saleCurrency: pick(raw, 'saleCurrency', 'sale_currency'),
    quickDiscounts: pick(raw, 'quickDiscounts', 'quick_discounts'),
    quickDiscountEnabled: pick(raw, 'quickDiscountEnabled', 'quick_discount_enabled'),
    quickDiscountOptional: pick(raw, 'quickDiscountOptional', 'quick_discount_optional'),
    branchPaymentMethods: pick(raw, 'branchPaymentMethods', 'branch_payment_methods'),
    retailCashbackPercent: pick(raw, 'retailCashbackPercent', 'retail_cashback_percent'),
  };
  return sanitizeAppPreferences(normalized);
}

export async function updateOperationalSettings(payload) {
  const clean = sanitizeAppPreferences(payload || {});
  const body = {
    pricingMode: clean.pricingMode,
    pricing_mode: clean.pricingMode,
    defaultMarkupPercent: clean.defaultMarkupPercent,
    default_markup_percent: clean.defaultMarkupPercent,
    dailySummaryTime: clean.dailySummaryTime,
    daily_summary_time: clean.dailySummaryTime,
    enabledSaleUnits: clean.enabledSaleUnits,
    enabled_sale_units: clean.enabledSaleUnits,
    purchaseCurrency: clean.purchaseCurrency,
    purchase_currency: clean.purchaseCurrency,
    saleCurrency: clean.saleCurrency,
    sale_currency: clean.saleCurrency,
    quickDiscounts: clean.quickDiscounts,
    quick_discounts: clean.quickDiscounts,
    quickDiscountEnabled: clean.quickDiscountEnabled,
    quick_discount_enabled: clean.quickDiscountEnabled,
    quickDiscountOptional: clean.quickDiscountOptional,
    quick_discount_optional: clean.quickDiscountOptional,
    branchPaymentMethods: clean.branchPaymentMethods,
    branch_payment_methods: clean.branchPaymentMethods,
    retailCashbackPercent: clean.retailCashbackPercent,
    retail_cashback_percent: clean.retailCashbackPercent,
  };
  const res = await api.put(endpoints.settingsOperational, body);
  const raw = unwrapOperationalPayload(res) || unwrapData(res) || body;
  const normalized = {
    pricingMode: pick(raw, 'pricingMode', 'pricing_mode', clean.pricingMode),
    defaultMarkupPercent: pick(raw, 'defaultMarkupPercent', 'default_markup_percent', clean.defaultMarkupPercent),
    dailySummaryTime: pick(raw, 'dailySummaryTime', 'daily_summary_time', clean.dailySummaryTime),
    enabledSaleUnits: pick(raw, 'enabledSaleUnits', 'enabled_sale_units', clean.enabledSaleUnits),
    purchaseCurrency: pick(raw, 'purchaseCurrency', 'purchase_currency', clean.purchaseCurrency),
    saleCurrency: pick(raw, 'saleCurrency', 'sale_currency', clean.saleCurrency),
    quickDiscounts: pick(raw, 'quickDiscounts', 'quick_discounts', clean.quickDiscounts),
    quickDiscountEnabled: pick(raw, 'quickDiscountEnabled', 'quick_discount_enabled', clean.quickDiscountEnabled),
    quickDiscountOptional: pick(raw, 'quickDiscountOptional', 'quick_discount_optional', clean.quickDiscountOptional),
    branchPaymentMethods: pick(raw, 'branchPaymentMethods', 'branch_payment_methods', clean.branchPaymentMethods),
    retailCashbackPercent: pick(raw, 'retailCashbackPercent', 'retail_cashback_percent', clean.retailCashbackPercent),
  };
  return sanitizeAppPreferences(normalized);
}

export async function getCurrencyRates() {
  const res = await api.get(endpoints.systemCurrencyRates);
  const raw = unwrapData(res) || {};
  const ratesRaw = raw?.rates && typeof raw.rates === 'object' ? raw.rates : {};
  const safeRates = { UZS: 1 };
  Object.keys(ratesRaw).forEach((k) => {
    const code = String(k || '').toUpperCase();
    const n = Number((ratesRaw)[k]);
    if (code && Number.isFinite(n)) {
      safeRates[code] = n;
    }
  });
  return {
    base: safeStr(raw.base, 'UZS'),
    currencies: asArray(raw.currencies).map((x) => String(x).toUpperCase()),
    rates: safeRates,
    updatedAt: safeStr(raw.updatedAt, ''),
    fetchedAt: safeStr(raw.fetchedAt, ''),
    source: safeStr(raw.source, ''),
    stale: !!raw.stale,
  };
}
